import config from '@app/config'
import type { BaseServerContext } from '@app/serverMiddleware/AppServerContext'

/**
 * Emits a host-only deletion (no `Domain` attribute) for `name`. A cookie set
 * without a `Domain` attribute can only be dropped by a `Set-Cookie` that also
 * omits it — a `COOKIE_DOMAIN`-scoped delete misses it. Used to clear cookies
 * left over from before `COOKIE_DOMAIN` existed.
 */
export function clearHostOnlyCookie(ctx: BaseServerContext, name: string): void {
  ctx.cookies.set(name, null, { path: '/', overwrite: true, secure: config.secureCookie, httpOnly: false })
}

export function migrateHostOnlyCookies(ctx: BaseServerContext): void {
  if (!process.env.COOKIE_DOMAIN) return

  const raw = ctx.request.headers.cookie
  if (!raw) return

  const pairs = parsePairs(raw)
  if (pairs.length === 0) return

  const counts = new Map<string, number>()
  for (const { name } of pairs) counts.set(name, (counts.get(name) ?? 0) + 1)

  const duplicateNames = Array.from(counts.entries())
    .filter(([, c]) => c > 1)
    .map(([n]) => n)
  if (duplicateNames.length === 0) return

  ctx.request.headers.cookie = dedupeKeepLast(pairs)

  for (const name of duplicateNames) {
    clearHostOnlyCookie(ctx, name)
  }
}

type Pair = { name: string; value: string }

function parsePairs(raw: string): Pair[] {
  const out: Pair[] = []
  for (const part of raw.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    const name = part.slice(0, eq).trim()
    if (!name) continue
    out.push({ name, value: part.slice(eq + 1).trim() })
  }
  return out
}

function dedupeKeepLast(pairs: Pair[]): string {
  const seen = new Set<string>()
  const kept: Pair[] = []
  for (let i = pairs.length - 1; i >= 0; i--) {
    const p = pairs[i]
    if (seen.has(p.name)) continue
    seen.add(p.name)
    kept.push(p)
  }
  kept.reverse()
  return kept.map(p => `${p.name}=${p.value}`).join('; ')
}
